<script>
import fetch from '@/fetch'
import { Debounce } from '@/utils'

import ChecklistComponent from '@/components/Select/Checklist'
import { InputComponent } from 'vue-elder-input'

const mapToString = ({ onModel, ref, parent }) => [onModel, ref, parent].filter(Boolean).join(':')
const mapToObject = v => {
  const [onModel, ref, parent] = v.split(':')
  const result = { onModel, ref }
  if (parent) result.parent = parent
  return result
}

const getArrayDiff = (n = [], o = []) => {
  const added = n.filter(x => !o.includes(x)).map(mapToObject)
  const removed = o.filter(x => !n.includes(x)).map(mapToObject)
  return { added, removed }
}

export default {
  props: {
    value: Array,
    filter: Object,
    initialCurrent: String,
  },
  watch: {
    itemsFilter: {
      handler: Debounce(function () {
        this.fetchItems()
      }, 200),
      immediate: true,
      deep: true,
    },
    current: {
      handler: 'fetchSubitems',
      immediate: true,
    },
    initialCurrent: {
      handler(val) {
        const target = this.items.find(i => i.id === val)
        if (!target) return
        this.current = target
        this.scrollToItem(target.id)
      },
    },
    subitemsFilter: {
      handler: Debounce(function () {
        this.fetchSubitems()
      }, 200),
      deep: true,
    },
  },
  data() {
    return {
      promises: { items: null, subitems: null },
      unsortedItems: [],
      subitems: [],
      current: null,
      filters: { items: { search: null }, subitems: { search: null } },
    }
  },
  computed: {
    items() {
      return this.unsortedItems.sort((a, b) => a.title.localeCompare(b.title))
    },
    itemsFilter() {
      return {
        ...this.filter,
        ...this.filters.items,
      }
    },
    subitemsFilter() {
      return {
        ...this.filters.subitems,
      }
    },
    valueComp: {
      get() {
        return this.value.map(mapToString)
      },
      set(val) {
        const { added } = getArrayDiff(val, this.valueComp)
        let items = val.map(mapToObject)

        this.$emit('input', items)
      },
    },
  },
  methods: {
    // onCheckboxIconClick(item) {
    //   const id = `Project:${item.id}`
    //   if (this.valueComp.includes(id)) this.valueComp = this.valueComp.filter(v => v !== id)
    //   else this.valueComp = [...this.valueComp, id]
    // },
    getCount(item) {
      return this.value.filter(i => [i.ref, i.parent].includes(item.id)).length
    },
    fetchItems() {
      const url = new URL(location.origin + '/api/resource/project')
      url.searchParams.append('sort.name', '1')
      Object.entries(this.itemsFilter).forEach(([k, v]) => {
        if (![null, undefined].includes(v)) url.searchParams.append(k, v)
      })

      this.promises.items = fetch(url.toString())
        .then(res => res.json())
        .then(items => {
          this.unsortedItems = items
          const initial = this.items.find(i => this.initialCurrent && i.id === this.initialCurrent)
          this.current = initial || this.items[0]
          if (initial) this.scrollToItem(initial.id)
        })
    },
    fetchSubitems() {
      if (!this.current) return

      const url = new URL(location.origin + '/api/resource/residential')
      url.searchParams.set('project', this.current.id)
      url.searchParams.set('field.label', 'displayName|number')
      url.searchParams.set('field.value', 'Residential:{id}:{project}')
      url.searchParams.append('sort.displayName', '1')

      Object.entries(this.subitemsFilter).forEach(([k, v]) => {
        if (![null, undefined].includes(v)) url.searchParams.append(k, v)
      })

      this.promises.subitems = fetch(url.toString())
        .then(res => res.json())
        .then(
          items =>
            (this.subitems = [
              { label: this.$t('leadScopeProjectLabel'), value: `Project:${this.current.id}` },
              ...items.map(i => ({ ...i, label: String(i.label) })),
            ]),
        )
    },
    scrollToItem(id) {
      this.$nextTick(() => {
        const el = this.$el.querySelector(`[data-id="${id}"]`)
        const first = this.$el.querySelector('.lead-scopes__item')
        el.closest('.lead-scopes__pane').scrollTop = el.offsetTop - first.offsetTop
      })
    },
  },
  components: {
    ChecklistComponent,
    InputComponent,
  },
}
</script>

<template>
  <div class="lead-scopes">
    <Loader :value="promises.items" class="lead-scopes__pane">
      <InputComponent
        v-model="filters.items.search"
        :placeholder="$t('search') + '...'"
        icon="fa-pro-light:search"
        class="lead-scopes__search"
      />
      <button
        v-for="item in items"
        :class="['lead-scopes__item', { 'lead-scopes__item--active': current === item }]"
        :style="{ '--bg': `url('/api/project/${item.id}/cover')` }"
        type="button"
        :data-id="item.id"
        @click="() => (current = item)"
      >
        <Icon
          :style="{ color: getCount(item) ? 'var(--success)' : 'currentColor' }"
          :icon="getCount(item) ? 'fa-pro-solid:check-circle' : 'fa-pro-thin:circle'"
          size="lg"
        />
        <label>{{ item.title }}</label>
        <Icon icon="fa-pro-solid:angle-right" size="lg" />
      </button>
    </Loader>
    <div class="lead-scopes__divider"></div>
    <Loader :value="promises.subitems" class="lead-scopes__pane">
      <InputComponent
        v-model="filters.subitems.search"
        :placeholder="$t('search') + '...'"
        icon="fa-pro-light:search"
        class="lead-scopes__search"
      />
      <ChecklistComponent v-model="valueComp" :items="subitems" />
    </Loader>
  </div>
</template>

<style lang="scss">
.lead-scopes {
  display: grid;
  grid-template-columns: 1fr 1px 1fr;
  gap: 1.5rem;

  &__pane {
    display: flex;
    flex-direction: column;
    flex-grow: 1;

    gap: 1rem;

    max-height: 70.5vh;
    overflow-y: auto;

    &:has(.lead-scopes__item--active) {
      .lead-scopes__item:not(.lead-scopes__item--active) {
        background-image: linear-gradient(to right, rgba(white, 0.95) 0%, rgba(white, 0.85) 50%), var(--bg);
        color: black;
      }
    }

    .elder-checkbox:first-child:not(:last-child) {
      padding-bottom: 0.75rem;
      margin-bottom: 0.25rem;
      border-bottom: 1px solid var(--border-color);
    }
  }

  &__divider {
    width: 1px;
    background-color: var(--border-color);
    flex-shrink: 0;
    align-self: stretch;
  }

  &__item {
    background-color: var(--primary);
    background-image: linear-gradient(to right, rgba(black, 0.9) 0%, rgba(black, 0.7) 50%, transparent 100%), var(--bg);
    background-size: cover;
    background-position: center;
    border-radius: var(--border-radius);
    text-align: left;

    display: flex;
    align-items: center;
    justify-content: space-between;

    flex-shrink: 0;

    gap: 1rem;

    font-size: 1.2rem;
    font-weight: bold;
    color: white;

    border: 1px solid var(--k-ui-color-neutral);

    padding-inline: 1.5rem;

    height: 6rem;

    cursor: pointer;

    transition-property: height, filter, color, background-image;
    transition-duration: 200ms;
    transition-timing-function: ease-in-out;

    svg {
      font-size: 1.5rem;
      position: relative;
      top: 1px;
      flex-shrink: 0;
    }

    label {
      margin-right: auto;
      line-height: 1;
      pointer-events: none;
    }

    [data-count]:not([data-count='0']) {
      background-color: var(--primary);
      color: var(--primary-contrast);
      border-radius: 99px;
      padding: 0.1em 0.5em;
      font-size: 0.8em;
      text-align: center;

      display: inline-flex;
      align-items: center;
      justify-content: center;

      &:before {
        content: attr(data-count);
      }
    }
  }

  [data-part='empty'] {
    opacity: 0.6;
  }

  &__search {
    position: sticky;
    top: 0;
    z-index: 2;
  }
}
</style>
